import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { AbstractControl, AsyncValidatorFn, ValidationErrors } from '@angular/forms';
import { Observable, of, timer } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { ConfigService } from '../config.service';
import { PolicyTemplateSummary } from '../../interfaces/policy-templates/policy-template-summary.interface';
import { PolicyTemplateDetail } from '../../interfaces/policy-templates/policy-template-detail.interface';
import { CreatePolicyTemplateRequest } from '../../interfaces/policy-templates/create-policy-template-request.interface';
import { RenamePolicyTemplateRequest } from '../../interfaces/policy-templates/rename-policy-template-request.interface';
import { DuplicateCheckRequest, DuplicateCheckResponse } from '../../interfaces/policy-templates/duplicate-check.interface';

@Injectable({ providedIn: 'root' })
export class PolicyTemplateService {
  private readonly baseUrl: string;
  private readonly classesBaseUrl: string;

  constructor(
    private readonly http: HttpClient,
    private readonly configService: ConfigService,
  ) {
    const apiBase = this.configService.getApiUrl();
    this.baseUrl      = `${apiBase}/v1/policy-templates`;
    this.classesBaseUrl = `${apiBase}/v1/classes`;
  }

  /** List all policy templates (summary projection — no step data). */
  getAll(): Observable<PolicyTemplateSummary[]> {
    return this.http.get<PolicyTemplateSummary[]>(this.baseUrl);
  }

  /** Get a single policy template with full step detail. */
  getById(id: string): Observable<PolicyTemplateDetail> {
    return this.http.get<PolicyTemplateDetail>(`${this.baseUrl}/${id}`);
  }

  /** List policy templates filtered by resource type. */
  getByType(resourceType: 'Vm' | 'UserAccount'): Observable<PolicyTemplateSummary[]> {
    return this.http.get<PolicyTemplateSummary[]>(`${this.baseUrl}/by-type/${resourceType}`);
  }

  /**
   * Check if a policy name is already taken.
   * Consumed by policyNameUniqueValidator with a 500 ms debounce.
   * Pass excludeId to allow a rename to keep its own current name.
   */
  nameExists(name: string, resourceType?: string, excludeId?: string): Observable<{ exists: boolean }> {
    let params = new HttpParams().set('name', name);
    if (resourceType) params = params.set('resourceType', resourceType);
    if (excludeId) params = params.set('excludeId', excludeId);
    return this.http.get<{ exists: boolean }>(`${this.baseUrl}/exists`, { params });
  }

  /**
   * Content-hash duplicate check. Called at the wizard Review step on activation.
   * Returns the existing policy summary if a structurally identical policy already
   * exists, or { isDuplicate: false, existing: null } for a novel configuration.
   * Pass excludeId when re-checking during a rename scenario (self-exclusion).
   */
  checkDuplicate(
    request: DuplicateCheckRequest,
    excludeId?: string,
  ): Observable<DuplicateCheckResponse> {
    let params = new HttpParams();
    if (excludeId) params = params.set('excludeId', excludeId);
    return this.http.post<DuplicateCheckResponse>(
      `${this.baseUrl}/check-duplicate`,
      request,
      { params },
    );
  }

  /** Create a new policy template. Returns the full detail DTO on success. */
  create(request: CreatePolicyTemplateRequest): Observable<PolicyTemplateDetail> {
    return this.http.post<PolicyTemplateDetail>(this.baseUrl, request);
  }

  /**
   * Rename a policy template (name + description only).
   * Policy steps are immutable after creation — see immutability design principle.
   */
  rename(id: string, request: RenamePolicyTemplateRequest): Observable<PolicyTemplateDetail> {
    return this.http.patch<PolicyTemplateDetail>(`${this.baseUrl}/${id}/rename`, request);
  }

  /** Delete a policy template. Completes with no value on success (204 No Content). */
  delete(id: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${id}`);
  }

  /**
   * Returns the classes that currently have this policy template linked to one of their
   * resource template entries (i.e. TrainingSchedule.ResourceTemplateIds[].PolicyTemplateId).
   */
  getLinkedClasses(policyTemplateId: string): Observable<PolicyClassEntry[]> {
    return this.http.get<PolicyClassEntry[]>(`${this.baseUrl}/${policyTemplateId}/linked-classes`);
  }

  /**
   * Links policy templates to a class by patching PolicyTemplateId on the
   * matching resource-type entries in TrainingSchedule.ResourceTemplateIds.
   */
  linkToClass(classId: string, links: ResourcePolicyLink[]): Observable<{ success: boolean }> {
    return this.http.put<{ success: boolean }>(
      `${this.classesBaseUrl}/${classId}/policy-linkage`,
      { resourcePolicyLinks: links },
    );
  }

  /**
   * Archives a policy template (soft-delete). Blocked by the server (409) if any
   * linked class is still active.
   */
  archive(id: string): Observable<void> {
    return this.http.patch<void>(
      `${this.baseUrl}/${encodeURIComponent(id)}/archive`,
      null
    );
  }
}

export interface PolicyClassEntry {
  classId: string;
  className: string;
  /** True when this class has already been soft-archived. */
  isArchived: boolean;
}

export interface ResourcePolicyLink {
  resourceType: string;
  policyTemplateId: string;
}

/**
 * Reusable async validator factory for policy name uniqueness.
 * Debounces 500 ms to avoid hammering the API on every keystroke.
 * Returns { nameTaken: true } when the server reports the name is already in use.
 *
 * Usage:
 *   this.nameControl.addAsyncValidators(policyNameUniqueValidator(this.policyTemplateService));
 *   // For rename: pass the current policy id for self-exclusion
 *   this.nameControl.addAsyncValidators(policyNameUniqueValidator(this.policyTemplateService, policyId));
 */
export function policyNameUniqueValidator(
  service: PolicyTemplateService,
  resourceType?: string,
  excludeId?: string,
): AsyncValidatorFn {
  return (control: AbstractControl): Observable<ValidationErrors | null> => {
    const name = (control.value ?? '').trim();
    if (!name) return of(null);
    return timer(500).pipe(
      switchMap(() => service.nameExists(name, resourceType, excludeId)),
      map(res => (res.exists ? { nameTaken: true } : null)),
    );
  };
}
